import { createFileRoute } from "@tanstack/react-router";
import { serviceSchemaScripts } from "@/lib/service-schema";
import { freshnessMeta, webPageSchemaScript } from "@/lib/page-freshness";
import { FaqSection, faqSchemaScript, type FaqItem } from "@/components/landing/FaqSection";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { ParticleBackground } from "@/components/landing/ParticleBackground";
import { PageIntro } from "@/components/landing/PageIntro";
import { EventLandingPagesSection } from "@/components/landing/EventLandingPagesSection";
import { PromotionSection } from "@/components/landing/PromotionSection";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { ClosingCTA } from "@/components/landing/ClosingCTA";
import { RelatedServices } from "@/components/landing/RelatedServices";
import pioneerEventsImg from "@/assets/pioneer-events-mockup-v2.webp";

const PUBLISHED = "2026-08-29T00:00:00+00:00";
const MODIFIED = "2026-09-03T00:00:00+00:00";

const TITLE = "Landing Pages & Event RSVP Pages | Square Migration";
const DESCRIPTION =
  "Conversion-focused landing pages and event RSVP pages with confirmation emails, internal lead notifications, and a self-service admin backend for attendee access.";

const FAQS: FaqItem[] = [
  {
    "question": "How fast can an event landing page go live?",
    "answer": "Most event pages are live in days. You send the date, venue, agenda and audience, and you review a working URL rather than a mockup."
  },
  {
    "question": "Do event pages handle RSVPs and confirmation emails?",
    "answer": "Yes. Every event build includes an RSVP form, a confirmation email to the guest, and an instant internal notification to your team."
  },
  {
    "question": "Can our team access the guest list?",
    "answer": "Yes, event builds include a simple admin view so your team can pull the attendee list at any time without asking us."
  },
  {
    "question": "Do you build non-event landing pages too?",
    "answer": "Yes. Campaign pages, product launches and paid-traffic landing pages are all built the same way, with clean URLs and fast mobile load."
  }
];

export const Route = createFileRoute("/event-landing-pages")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://www.squaremigration.com/event-landing-pages" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:image", content: "https://www.squaremigration.com/og-image.jpg" },
      { name: "twitter:image", content: "https://www.squaremigration.com/og-image.jpg" },
      ...freshnessMeta({ published: PUBLISHED, modified: MODIFIED }),
    ],
    links: [{ rel: "canonical", href: "https://www.squaremigration.com/event-landing-pages" }],
    scripts: [...serviceSchemaScripts({
      path: "/event-landing-pages",
      name: "Landing Pages & Event RSVP Pages",
      description: DESCRIPTION,
      serviceType: "Landing page design and development",
    }), faqSchemaScript(FAQS), webPageSchemaScript({
      path: "/event-landing-pages",
      name: TITLE,
      description: DESCRIPTION,
      published: PUBLISHED,
      modified: MODIFIED,
    })],
  }),
  component: EventLandingPagesPage,
});

function EventLandingPagesPage() {
  return (
    <main className="relative">
      <ParticleBackground />
      <Header />
      <PageIntro
        eyebrow="Landing Pages"
        title="Quick-turn landing pages built to convert."
        description="Campaign, product, and event pages that go live in days, not months. Event builds include RSVP handling, confirmation emails, internal lead notifications, and an admin backend for attendee access."
        bullets={["Campaign pages", "Event RSVP flows", "Confirmation emails", "Lead notifications"]}
        image={{
          src: pioneerEventsImg,
          alt: "Pioneer event landing pages shown on three tablets",
        }}
      />
      <EventLandingPagesSection
        eyebrow="Page Types"
        headline="Pages built for one job: get the RSVP."
        lead="Campaign pages, product launches, and invite-only events, each built around a single action. Event builds add RSVP confirmation emails, instant internal notifications, and a simple admin view so your team can pull the guest list without asking us."
      />
      <PromotionSection
        eyebrow="Driving Traffic"
        headline={
          <>
            A page is only as good as{"\n"}
            <span className="text-primary">the traffic you send it</span>
          </>
        }
        lead="We build every page so it drops cleanly into whatever channel you’re already using to fill the room."
        note="Clean URLs, fast mobile load, and copy that survives being pasted into an email or an ad, so the same page works for outbound, paid, and a partner’s newsletter."
      />
      <ProcessSection
        eyebrow="Launch Timeline"
        title="Event pages go live in days, not sprints."
        lead="Most invites are time-sensitive, so the process is built for speed: send us the details and review a working page, not a mockup."
        steps={[
          { number: "01", title: "Send the Details", description: "Date, venue, agenda, speakers, logos and the audience you’re inviting." },
          { number: "02", title: "Review a Live Page", description: "You get a working URL to click through, copy, layout and RSVP form all functional." },
          { number: "03", title: "Wire Up Notifications", description: "Confirmation email to the guest, instant alert to your team, guest list in the admin view." },
          { number: "04", title: "Promote & Track", description: "Launch it across email and LinkedIn, then watch RSVPs land in real time." },
        ]}
      />
      <ClosingCTA
        eyebrow="Have an Event Coming Up?"
        headline={
          <>
            Don’t send another <span className="text-primary">calendar invite.</span>
          </>
        }
        subhead="Send a page worth clicking."
        body="Tell us the date, the venue and who you’re inviting. We’ll have a branded RSVP page with confirmation emails and lead alerts ready before your promo goes out."
        ctaLabel="Plan an Event Page"
      />
      <FaqSection items={FAQS} />
      <RelatedServices current="/event-landing-pages" />
      <Footer />
    </main>
  );
}
